import React, { useState } from 'react';
import classnames from 'classnames';
import { CardTitle } from 'reactstrap';
import VisibilitySensor from 'react-visibility-sensor';
import { getConfig } from 'config';
import __ from 'utils/i18n';
import Loader from 'components/Loader';
import FlexTable from 'components/FlexTable';
import DashboardPanel from 'containers/Dashboard/components/DashboardPanel';
import { SalePanel } from '../SalePanel';
import { SubsidiaryCell } from './SubsidiaryCell';
import { SaleDescriptionCell } from './DescriptionAndStatusCell';
import { SalePaymentCell } from './PaymentCell';
import { SaleCreationDate } from './CreationDateCell';
import { TotalAmountCell } from './TotalAmountCell';
import { CashbackAmountCell } from './CashbackAmountCell';
import { InstallmentTypeCell } from './InstallmentTypeCell';
import { OperationCode } from './OperationCodeCell';
import { StatusCell } from './BatchStatusCell';
import { terminalPayment } from './TerminalPaymentCell';

const MovementTypeCell = ({ original }) => {
  const getMovementType = (sale) => {
    if (sale.refunded) return __('dashboard.sections.sales.movementTypes.refund') || 'Devolución';
    if (sale.adjustment) return __('dashboard.sections.sales.movementTypes.adjustment') || 'Ajuste';
    if (sale.payment) return __('dashboard.sections.sales.movementTypes.sale') || 'Venta';
    return __('dashboard.sections.sales.movementTypes.transaction') || 'Transacción';
  };

  return (
    <span className="movement-type-cell">
      {getMovementType(original)}
    </span>
  );
};

const CardBrandCell = ({ original }) => {
  const getCardBrandInfo = (sale) => {
    const brand = sale.payment?.cardBrand || sale.cardBrand;
    if (!brand) return { name: '-', logo: null };

    const brandLower = brand.toLowerCase();

    const brandMap = {
      'visa': {
        name: 'Visa',
        logo: 'https://upload.wikimedia.org/wikipedia/commons/4/41/Visa_Logo.png'
      },
      'mastercard': {
        name: 'Mastercard',
        logo: 'https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg'
      },
      'amex': {
        name: 'American Express',
        logo: 'https://upload.wikimedia.org/wikipedia/commons/3/30/American_Express_logo.svg'
      },
      'cabal': {
        name: 'Cabal',
        logo: 'https://www.cabal.com.ar/assets/img/logo-cabal.svg'
      },
      'maestro': {
        name: 'Maestro',
        logo: 'https://upload.wikimedia.org/wikipedia/commons/0/04/Maestro_logo.svg'
      },
      'diners': {
        name: 'Diners Club',
        logo: 'https://upload.wikimedia.org/wikipedia/commons/6/6b/Diners_Club_logo.svg'
      }
    };

    return brandMap[brandLower] || { name: brand, logo: null };
  };

  const brandInfo = getCardBrandInfo(original);

  return (
    <div className="card-brand-cell">
      {brandInfo.logo ? (
        <img
          src={brandInfo.logo}
          alt={brandInfo.name}
          title={brandInfo.name}
          style={{
            height: '20px',
            width: 'auto',
            maxWidth: '40px'
          }}
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'inline';
          }}
        />
      ) : null}
      <span style={{ display: brandInfo.logo ? 'none' : 'inline' }}>
        {brandInfo.name}
      </span>
    </div>
  );
};

export const getSalesColumns = (subsidiaries) => {
  const configColumns = getConfig('tableColumns.salesColumns');
  const salesColumns = [];

  const defaultColumns = {
    subsidiary: {
      Header: 'sucursal',
      Cell: SubsidiaryCell(subsidiaries),
    },
    description: {
      id: 'productName',
      Header: __('dashboard.sections.sales.description'),
      Cell: SaleDescriptionCell,
    },
    movementType: {
      id: 'movementType',
      Header: __('dashboard.sections.sales.movementType') || 'Tipo de movimiento',
      Cell: MovementTypeCell,
      width: 150
    },
    cardBrand: {
      id: 'cardBrand',
      Header: __('dashboard.sections.sales.cardBrand') || 'Marca de tarjeta',
      Cell: CardBrandCell,
      width: 150
    },
    payment: {
      Header: __('dashboard.sections.sales.paymentMethodType'),
      Cell: SalePaymentCell,
    },
    date: {
      Header: __('dashboard.sections.sales.date'),
      Cell: SaleCreationDate,
    },
    amount: {
      Header: __('dashboard.sections.sales.amount'),
      Cell: TotalAmountCell,
    },
    cashback: {
      Header: __('dashboard.sections.sales.cashback'),
      Cell: CashbackAmountCell,
    },
    deferredPayments: {
      Header: __('dashboard.sections.sales.deferredPayments'),
      Cell: InstallmentTypeCell,
    },
    operationCode: {
      Header: __('dashboard.sections.sales.operationCode'),
      Cell: OperationCode,
    },
    status: {
      Header: __('dashboard.sections.sales.status'),
      Cell: StatusCell,
    },
    terminalPayment: {
      Header:__('dashboard.sections.sales.terminalPayment'),
      Cell: terminalPayment,
    },
  };

  // Columns are fully config-driven: each tenant lists the columns it wants in
  // `tableColumns.salesColumns` (e.g. ASJ lists "movementType"/"cardBrand" directly).
  configColumns.forEach((item) => salesColumns.push(defaultColumns[item]));

  return salesColumns;
};

export const SalesTable = ({
  data,
  isFetching,
  hasMore,
  getMore,
  error,
  subsidiariesStore,
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [detailsData, setDetailsData] = useState({});

  const handleDetails = (data) => {
    if (
      data.type !== 'ADJUSTMENT' &&
      data.type !== 'COMMISSION' &&
      data.type !== 'ADJUSTMENT_DEBIT' &&
      data.type !== 'ADJUSTMENT_CREDIT'
    ) {
      setShowDetails(true);
      setDetailsData(data);
    }
  };

  return (
    <div
      className={classnames({
        'panel-expanded': showDetails,
        'table-container': true,
      })}
    >
      <DashboardPanel
        panelHeading={`${
          detailsData.showInvoiceNumber
            ? __('dashboard.sections.sales.bill')
            : __('dashboard.sections.sales.receipt')
        } 
            ${detailsData.operationCode}`}
        expanded={showDetails}
        hidePanel={() => setShowDetails(false)}
      >
        {showDetails && <SalePanel data={detailsData} />}
      </DashboardPanel>
      {data.length > 0 && (
        <>
          {data.some((item) => item.status === 'rejected') && (
            <CardTitle tag='div'>
              {__('dashboard.sections.sales.rejected.description')}
            </CardTitle>
          )}
          <FlexTable
            data={data}
            columns={getSalesColumns(subsidiariesStore)}
            onRowClick={({ index }) => handleDetails(data[index])}
          />
        </>
      )}
      <VisibilitySensor onChange={(visible) => visible && !error && getMore()}>
        <div style={{ height: 1, width: '100%' }} />
      </VisibilitySensor>
      {hasMore && <div className='loader-row' data-testid="loader">{isFetching && <Loader />}</div>}
    </div>
  );
};
